import * as C from "../constants/chatConstants";

const EMPTY_UNREAD = {
  conversations: 0,
  messages: 0,
};

function getId(value = {}) {
  if (value && typeof value === "object") {
    return String(value.id || value._id || "").trim();
  }

  return String(value || "").trim();
}

function normalizeConversation(payload = {}) {
  if (
    payload &&
    typeof payload === "object" &&
    Object.prototype.hasOwnProperty.call(payload, "conversation")
  ) {
    return payload.conversation || null;
  }

  return payload || null;
}

function normalizeMessage(payload = {}) {
  if (
    payload &&
    typeof payload === "object" &&
    Object.prototype.hasOwnProperty.call(payload, "message")
  ) {
    return payload.message || null;
  }

  return payload || null;
}

function normalizeMessages(payload = {}) {
  return Array.isArray(payload?.messages) ? payload.messages : [];
}

function appendMessage(messages = [], message = null) {
  const messageId = getId(message);

  if (!message || !messageId) return messages;

  const exists = messages.some((item) => getId(item) === messageId);

  if (exists) {
    return messages.map((item) => (getId(item) === messageId ? message : item));
  }

  return [...messages, message];
}

function sortConversations(conversations = []) {
  return [...conversations].sort((a, b) => {
    const aDate = new Date(a?.lastMessageAt || a?.updatedAt || 0).getTime();
    const bDate = new Date(b?.lastMessageAt || b?.updatedAt || 0).getTime();

    return bDate - aDate;
  });
}

function conversationMatchesFilter(conversation = null, statusFilter = "open") {
  if (!conversation) return false;

  const filter = String(statusFilter || "open").toLowerCase();

  if (filter === "all") return true;

  return String(conversation.status || "open").toLowerCase() === filter;
}

function upsertConversation(
  conversations = [],
  conversation = null,
  statusFilter = "open",
) {
  const conversationId = getId(conversation);

  if (!conversationId) return conversations;

  const withoutCurrent = conversations.filter(
    (item) => getId(item) !== conversationId,
  );

  if (!conversationMatchesFilter(conversation, statusFilter)) {
    return withoutCurrent;
  }

  return sortConversations([conversation, ...withoutCurrent]);
}

function sameConversation(current = null, next = null) {
  const currentId = getId(current);
  const nextId = getId(next);

  return Boolean(currentId && nextId && currentId === nextId);
}

export const customerChatReducer = (
  state = {
    loading: false,
    sending: false,
    identifying: false,
    resending: false,
    clearingIdentity: false,
    conversation: null,
    messages: [],
    verificationRequired: false,
    emailVerified: false,
    error: null,
  },
  action,
) => {
  switch (action.type) {
    case C.CUSTOMER_CHAT_CURRENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case C.CUSTOMER_CHAT_START_REQUEST:
    case C.CUSTOMER_CHAT_SEND_REQUEST:
      return {
        ...state,
        sending: true,
        error: null,
      };

    case C.CUSTOMER_CHAT_IDENTITY_REQUEST:
      return {
        ...state,
        identifying: true,
        error: null,
      };

    case C.CUSTOMER_CHAT_RESEND_REQUEST:
      return {
        ...state,
        resending: true,
        error: null,
      };

    case C.CUSTOMER_CHAT_CLEAR_IDENTITY_REQUEST:
      return {
        ...state,
        clearingIdentity: true,
        error: null,
      };

    case C.CUSTOMER_CHAT_CURRENT_SUCCESS:
    case C.CUSTOMER_CHAT_START_SUCCESS:
    case C.CUSTOMER_CHAT_IDENTITY_SUCCESS:
    case C.CUSTOMER_CHAT_RESEND_SUCCESS:
      return {
        ...state,
        loading: false,
        sending: false,
        identifying: false,
        resending: false,
        clearingIdentity: false,
        conversation: normalizeConversation(action.payload),
        messages: normalizeMessages(action.payload),
        verificationRequired: Boolean(action.payload?.verificationRequired),
        emailVerified: Boolean(action.payload?.emailVerified),
        error: null,
      };

    case C.CUSTOMER_CHAT_SEND_SUCCESS: {
      const conversation = normalizeConversation(action.payload);
      const message = normalizeMessage(action.payload);

      return {
        ...state,
        sending: false,
        conversation: conversation || state.conversation,
        messages: appendMessage(state.messages, message),
        verificationRequired: false,
        emailVerified: Boolean(
          conversation?.emailVerified || state.conversation?.emailVerified,
        ),
        error: null,
      };
    }

    case C.CUSTOMER_CHAT_CLEAR_IDENTITY_SUCCESS:
      return {
        ...state,
        loading: false,
        sending: false,
        identifying: false,
        resending: false,
        clearingIdentity: false,
        conversation: null,
        messages: [],
        verificationRequired: true,
        emailVerified: false,
        error: null,
      };

    case C.CUSTOMER_CHAT_READ_SUCCESS:
    case C.CUSTOMER_CHAT_CONVERSATION_UPDATE: {
      const conversation = normalizeConversation(action.payload);

      if (!sameConversation(state.conversation, conversation)) {
        return state;
      }

      return {
        ...state,
        conversation,
      };
    }

    case C.CUSTOMER_CHAT_RECEIVE_MESSAGE: {
      const conversation = normalizeConversation(action.payload);
      const message = normalizeMessage(action.payload);

      if (!sameConversation(state.conversation, conversation)) {
        return state;
      }

      return {
        ...state,
        conversation: conversation || state.conversation,
        messages: appendMessage(state.messages, message),
      };
    }

    case C.CUSTOMER_CHAT_CURRENT_FAIL:
    case C.CUSTOMER_CHAT_START_FAIL:
    case C.CUSTOMER_CHAT_IDENTITY_FAIL:
    case C.CUSTOMER_CHAT_RESEND_FAIL:
    case C.CUSTOMER_CHAT_CLEAR_IDENTITY_FAIL:
    case C.CUSTOMER_CHAT_SEND_FAIL:
      return {
        ...state,
        loading: false,
        sending: false,
        identifying: false,
        resending: false,
        clearingIdentity: false,
        error: action.payload,
      };

    case C.CUSTOMER_CHAT_RESET:
      return {
        loading: false,
        sending: false,
        identifying: false,
        resending: false,
        clearingIdentity: false,
        conversation: null,
        messages: [],
        verificationRequired: false,
        emailVerified: false,
        error: null,
      };

    default:
      return state;
  }
};

export const adminChatReducer = (
  state = {
    loading: false,
    detailsLoading: false,
    sending: false,
    statusUpdating: false,
    conversations: [],
    activeConversation: null,
    activeMessages: [],
    unread: EMPTY_UNREAD,
    statusFilter: "open",
    error: null,
    detailsError: null,
  },
  action,
) => {
  switch (action.type) {
    case C.ADMIN_CHAT_LIST_REQUEST:
      return {
        ...state,
        loading: true,
        statusFilter: action.meta?.status || state.statusFilter,
        error: null,
      };

    case C.ADMIN_CHAT_DETAILS_REQUEST:
      return {
        ...state,
        detailsLoading: true,
        detailsError: null,
      };

    case C.ADMIN_CHAT_SEND_REQUEST:
      return {
        ...state,
        sending: true,
        detailsError: null,
      };

    case C.ADMIN_CHAT_STATUS_REQUEST:
      return {
        ...state,
        statusUpdating: true,
        detailsError: null,
      };

    case C.ADMIN_CHAT_LIST_SUCCESS:
      return {
        ...state,
        loading: false,
        conversations: sortConversations(action.payload?.conversations || []),
        unread: action.payload?.unread || state.unread,
        statusFilter: action.meta?.status || state.statusFilter,
        error: null,
      };

    case C.ADMIN_CHAT_DETAILS_SUCCESS:
      return {
        ...state,
        detailsLoading: false,
        activeConversation: normalizeConversation(action.payload),
        activeMessages: normalizeMessages(action.payload),
        detailsError: null,
      };

    case C.ADMIN_CHAT_SEND_SUCCESS: {
      const conversation = normalizeConversation(action.payload);
      const message = normalizeMessage(action.payload);

      return {
        ...state,
        sending: false,
        activeConversation: sameConversation(state.activeConversation, conversation)
          ? conversation
          : state.activeConversation,
        activeMessages: sameConversation(state.activeConversation, conversation)
          ? appendMessage(state.activeMessages, message)
          : state.activeMessages,
        conversations: upsertConversation(
          state.conversations,
          conversation,
          state.statusFilter,
        ),
        detailsError: null,
      };
    }

    case C.ADMIN_CHAT_STATUS_SUCCESS:
    case C.ADMIN_CHAT_READ_SUCCESS: {
      const conversation = normalizeConversation(action.payload);

      return {
        ...state,
        statusUpdating: false,
        activeConversation: sameConversation(state.activeConversation, conversation)
          ? conversation
          : state.activeConversation,
        conversations: upsertConversation(
          state.conversations,
          conversation,
          state.statusFilter,
        ),
        detailsError: null,
      };
    }

    case C.ADMIN_CHAT_UNREAD_SUCCESS:
      return {
        ...state,
        unread: action.payload?.unread || EMPTY_UNREAD,
      };

    case C.ADMIN_CHAT_RECEIVE_MESSAGE: {
      const conversation = normalizeConversation(action.payload);
      const message = normalizeMessage(action.payload);

      return {
        ...state,
        activeConversation: sameConversation(state.activeConversation, conversation)
          ? conversation
          : state.activeConversation,
        activeMessages: sameConversation(state.activeConversation, conversation)
          ? appendMessage(state.activeMessages, message)
          : state.activeMessages,
        conversations: upsertConversation(
          state.conversations,
          conversation,
          state.statusFilter,
        ),
      };
    }

    case C.ADMIN_CHAT_CONVERSATION_UPDATE: {
      const conversation = normalizeConversation(action.payload);

      return {
        ...state,
        activeConversation: sameConversation(state.activeConversation, conversation)
          ? conversation
          : state.activeConversation,
        conversations: upsertConversation(
          state.conversations,
          conversation,
          state.statusFilter,
        ),
      };
    }

    case C.ADMIN_CHAT_LIST_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case C.ADMIN_CHAT_DETAILS_FAIL:
    case C.ADMIN_CHAT_SEND_FAIL:
    case C.ADMIN_CHAT_STATUS_FAIL:
      return {
        ...state,
        detailsLoading: false,
        sending: false,
        statusUpdating: false,
        detailsError: action.payload,
      };

    case C.ADMIN_CHAT_UNREAD_FAIL:
      return {
        ...state,
        unreadError: action.payload,
      };

    case C.ADMIN_CHAT_RESET_ACTIVE:
      return {
        ...state,
        activeConversation: null,
        activeMessages: [],
        detailsError: null,
      };

    default:
      return state;
  }
};
